import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useLocation, UNSAFE_NavigationContext } from 'react-router-dom'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material'

const NavigationBlockContext = createContext()

export const useNavigationBlock = () => {
  const context = useContext(NavigationBlockContext)
  if (!context) {
    throw new Error('useNavigationBlock must be used within a NavigationBlockProvider')
  }
  return context
}

// Export hook for backward compatibility with section components
export const useGlobalNavigationBlock = (isDirty) => {
  const { setIsDirty } = useNavigationBlock()

  useEffect(() => {
    setIsDirty(isDirty)
  }, [isDirty, setIsDirty])
}

export const NavigationBlockProvider = ({ children }) => {
  const [isDirty, setIsDirty] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [nextLocation, setNextLocation] = useState(null)
  const location = useLocation()
  const navigationContext = useContext(UNSAFE_NavigationContext)

  // Block browser navigation (refresh, close, back button via browser)
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault()
        event.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty])

  // Block in-app navigation by intercepting the history navigator
  useEffect(() => {
    if (!isDirty || !navigationContext?.navigator) {
      return
    }

    const { navigator } = navigationContext

    // Store original methods
    const originalPush = navigator.push.bind(navigator)
    const originalReplace = navigator.replace.bind(navigator)

    // Create interceptor function
    const createInterceptor = (originalMethod, methodName) => {
      return function (...args) {
        const [to] = args
        const nextPath = typeof to === 'object' ? to.pathname : to
        const currentPath = location.pathname

        // Allow navigation if it's to the same path or if user confirmed
        if (nextPath === currentPath || args[1]?.__navigationConfirmed) {
          return originalMethod(...args)
        }

        // Block navigation and show prompt
        setNextLocation({ method: methodName, args })
        setShowPrompt(true)
        return null
      }
    }

    // Override navigator methods
    navigator.push = createInterceptor(originalPush, 'push')
    navigator.replace = createInterceptor(originalReplace, 'replace')

    // Cleanup: restore original methods
    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
    }
  }, [isDirty, navigationContext, location.pathname])

  const handleConfirmNavigation = useCallback(() => {
    if (nextLocation && navigationContext?.navigator) {
      const { navigator } = navigationContext
      const { method, args } = nextLocation

      // Mark this navigation as confirmed to bypass the interceptor
      const [to, state] = args
      const confirmedState = { ...state, __navigationConfirmed: true }

      // Restore original methods temporarily and navigate
      const originalPush = Object.getPrototypeOf(navigator).push.bind(navigator)
      const originalReplace = Object.getPrototypeOf(navigator).replace.bind(navigator)

      if (method === 'push') {
        originalPush(to, confirmedState)
      } else {
        originalReplace(to, confirmedState)
      }
    }

    // Reset state
    setShowPrompt(false)
    setIsDirty(false)
    setNextLocation(null)
  }, [nextLocation, navigationContext])

  const handleCancelNavigation = useCallback(() => {
    setShowPrompt(false)
    setNextLocation(null)
  }, [])

  const value = {
    isDirty,
    setIsDirty,
  }

  return (
    <NavigationBlockContext.Provider value={value}>
      {children}
      <Dialog
        open={showPrompt}
        onClose={handleCancelNavigation}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to leave this page?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmNavigation} variant="contained" color="error">
            Leave Page
          </Button>
        </DialogActions>
      </Dialog>
    </NavigationBlockContext.Provider>
  )
}
